import dayjs from "dayjs";
import { v4 as uuidv4 } from "uuid";
import db from "../database";
import useSessionStore from "../stores/useSessionStore";
import { tracePreparedBill } from "./preparedBillRecovery";

/**
 * BUG-2574 (Dadus MR Kompally, TIC-12270): a table cart that still carries the sOrderID of a
 * Prepare Bill this device no longer has in db.orders (local DB wiped and refilled by a Full Sync,
 * KMRS/8151 at 20:41, wipe at 21:30), or one another device prepared. Every BUG-2425 / TIC-11390
 * check reads db.orders only, so the cart was treated as new: Prepare Bill and Pay re-sent the
 * booked id to posOrderProcessor1, which answers an id it already holds with 200 and no document
 * number -> "Server returned no document number" on every click.
 *
 * The server is asked once (salesHistoryDetails, same read as TSK-5163's cancel check). When it
 * holds this sOrderID as an open prepared bill -- docstatus CO, not invoiced, nothing paid, same
 * total as the cart -- a local row is written in the shape Prepare Bill itself saves
 * (preparedBillOrigin "Y", isSynced 1, the server's documentno), so the existing local checks
 * (BUG-2425 adoption, TIC-11390 lock, Cancel All -> confirmPOSLayawayOrder1) apply unchanged.
 * Anything else (not found, cancelled, invoiced, paid, different total, offline, timeout) returns
 * null and the cart is left as it is.
 */

const LOOKUP_TIMEOUT_MS = 5000;

const isServerId = (value) => typeof value === "string" && /^[A-Za-z0-9-]+$/.test(value);

const toAmount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : NaN;
};

const isYes = (value) => value === true || value === "Y" || value === "y" || value === "true";

const serverPaidAmount = (order) => {
  const plans = Array.isArray(order?.finReceiptPlan) ? order.finReceiptPlan : order?.finReceiptPlan ? [order.finReceiptPlan] : [];
  let paid = 0;
  for (const plan of plans) {
    const details = Array.isArray(plan?.finReceiptPlanDetails) ? plan.finReceiptPlanDetails : plan?.finReceiptPlanDetails ? [plan.finReceiptPlanDetails] : [];
    for (const d of details) {
      const amount = toAmount(d?.amount);
      if (Number.isFinite(amount)) paid += amount;
    }
  }
  return paid;
};

/** The reason a server order is NOT this cart's open prepared bill, or null when it is. */
export const whyNotOpenPreparedBill = (serverOrder, cart) => {
  if (!serverOrder?.sOrderID) return "not-found";
  if (serverOrder.sOrderID !== cart?.sOrderID) return "other-id";
  if (serverOrder.docstatus !== "CO") return `docstatus-${serverOrder.docstatus || "none"}`;
  if (isYes(serverOrder.isinvoiced)) return "invoiced";
  if (!serverOrder.documentno) return "no-documentno";
  if (Math.abs(serverPaidAmount(serverOrder)) > 0.005) return "paid";
  const serverTotal = toAmount(serverOrder.grosstotal);
  const cartTotal = toAmount(cart?.total);
  if (!Number.isFinite(serverTotal) || !Number.isFinite(cartTotal) || !(serverTotal > 0)) return "no-total";
  if (Math.abs(serverTotal - cartTotal) > 0.01) return "total-differs";
  return null;
};

const withTimeout = (promise, ms) =>
  new Promise((resolve) => {
    const timer = setTimeout(() => resolve({ timedOut: true }), ms);
    Promise.resolve(promise).then(
      (value) => {
        clearTimeout(timer);
        resolve({ value });
      },
      (error) => {
        clearTimeout(timer);
        resolve({ error });
      }
    );
  });

/**
 * Server state of one order. fetcher is services' salesHistoryDetails (passed in, as in
 * advanceInvoiceNoPrint.js). Returns { order } (order null = the server has no such order) or
 * { failed: reason } when the lookup could not be made -- never "not found" on a network error.
 */
export const fetchServerOrderState = async (sOrderID, fetcher, { timeoutMs = LOOKUP_TIMEOUT_MS } = {}) => {
  if (typeof fetcher !== "function" || !isServerId(sOrderID)) return { failed: "bad-input" };
  const query = {
    query: `query { salesHistoryDetails(orderId: "${sOrderID}") { sOrderID documentno docstatus isinvoiced grosstotal finReceiptPlan { finReceiptPlanDetails { amount } } } }`,
  };
  const { value: response, error, timedOut } = await withTimeout(fetcher(query), timeoutMs);
  if (timedOut) return { failed: "timeout" };
  if (error) return { failed: String(error?.message || error).slice(0, 200) };
  // salesHistoryDetails swallows network errors and returns undefined: no GraphQL envelope = unknown.
  if (!response?.data?.data || response.data.errors?.length) return { failed: "no-response" };
  return { order: response.data.data.salesHistoryDetails?.[0] || null };
};

/** The cart a table session can carry a server-only prepared bill for. */
export const needsServerPreparedBillCheck = (cart) =>
  !!cart?.tableId &&
  cart.preparedBillOrigin !== "Y" &&
  cart.isSplit !== "Y" &&
  isServerId(cart.sOrderID) &&
  (cart.items || []).some((i) => i?.fbOrderLineId);

/**
 * BUG-2574: when this dine-in cart's own sOrderID is an open prepared bill on the server and
 * db.orders has no row for it, save that row locally and return it ({ sOrderID, documentno, ... }).
 * Returns null in every other case. Never throws.
 */
export const restorePreparedBillFromServer = async (cart, fetcher, flow, options = {}) => {
  try {
    if (!needsServerPreparedBillCheck(cart)) return null;
    const localRow = await db.orders.get(cart.sOrderID);
    if (localRow) return null;
    const { order, failed } = await fetchServerOrderState(cart.sOrderID, fetcher, options);
    if (failed) {
      tracePreparedBill("server-prepared-bill-lookup-failed", { flow, sOrderID: cart.sOrderID, tableId: cart.tableId, reason: failed });
      return null;
    }
    const reason = whyNotOpenPreparedBill(order, cart);
    if (reason) {
      if (reason !== "not-found") {
        tracePreparedBill("server-prepared-bill-not-adopted", {
          flow,
          sOrderID: cart.sOrderID,
          tableId: cart.tableId,
          reason,
          serverDocumentno: order?.documentno || null,
          serverTotal: order?.grosstotal ?? null,
          cartTotal: cart.total ?? null,
        });
      }
      return null;
    }
    // Re-read: a payment or Prepare Bill on this device may have saved the row meanwhile.
    if (await db.orders.get(cart.sOrderID)) return null;
    const now = dayjs();
    const row = {
      ...cart,
      orderTime: cart.orderTime || now.format("DD-MM-YYYY HH:mm:ss"),
      tillSessionId: cart.tillSessionId || useSessionStore.getState().tillSession?.tillSessionId || "",
      key: cart.key || uuidv4().replace(/-/g, "").toUpperCase(),
      documentno: order.documentno,
      preparedBillOrigin: "Y",
      isPrepareBillCreate: "N",
      layAway: "N",
      paid: 0,
      payments: [],
      isSynced: 1,
      syncAttempts: 0,
      restoredFromServer: "Y",
    };
    await db.orders.put(row);
    tracePreparedBill("server-prepared-bill-restored", {
      flow,
      sOrderID: row.sOrderID,
      documentno: row.documentno,
      total: row.total,
      tableId: row.tableId,
    });
    return row;
  } catch (err) {
    tracePreparedBill("server-prepared-bill-restore-failed", { flow, sOrderID: cart?.sOrderID, error: String(err?.message || err).slice(0, 200) });
    return null;
  }
};

/**
 * BUG-2574: an online till (disableOfflineBilling "Y") sent a table cancel ("Cancel All") to
 * posOrderProcessor1 and got 200 with no document number. A new cancel gets its own number from
 * the server (KMRS/8170, CAN); no number means the server already holds this sOrderID and wrote
 * nothing. When that order is an open prepared bill (CO, not invoiced, nothing paid -- the total is
 * not compared, the cancel row is zeroed), the local cancel row is turned into a prepared-bill
 * cancel (the shape clearCart1 writes when the bill is known locally) and left pending, so the
 * next sync confirms it through confirmPOSLayawayOrder1 instead of it being marked sent while the
 * bill stays open in CW (KMRS/8151, 22:56). Returns the server's documentno, or null. Never throws.
 */
export const requeueCancelAsPreparedBillCancel = async (cancelRow, fetcher, options = {}) => {
  try {
    if (!cancelRow?.tableId || cancelRow.preparedBillOrigin === "Y" || !isServerId(cancelRow.sOrderID)) return null;
    const { order, failed } = await fetchServerOrderState(cancelRow.sOrderID, fetcher, options);
    if (failed) {
      tracePreparedBill("server-prepared-bill-cancel-lookup-failed", { sOrderID: cancelRow.sOrderID, tableId: cancelRow.tableId, reason: failed });
      return null;
    }
    const reason = whyNotOpenPreparedBill(order, { sOrderID: cancelRow.sOrderID, total: order?.grosstotal });
    if (reason) {
      if (reason !== "not-found") {
        tracePreparedBill("server-prepared-bill-cancel-not-requeued", { sOrderID: cancelRow.sOrderID, reason, serverDocumentno: order?.documentno || null });
      }
      return null;
    }
    await db.orders.where("sOrderID").equals(cancelRow.sOrderID).modify({
      documentno: order.documentno,
      preparedBillOrigin: "Y",
      preparedBillConfirmed: "Y",
      layAway: "Y",
      isPrepareBillCreate: "N",
      isSynced: 0,
      syncAttempts: 0,
    });
    tracePreparedBill("server-prepared-bill-cancel-requeued", {
      sOrderID: cancelRow.sOrderID,
      documentno: order.documentno,
      localDocumentno: cancelRow.documentno || null,
      tableId: cancelRow.tableId,
    });
    return order.documentno;
  } catch (err) {
    tracePreparedBill("server-prepared-bill-cancel-requeue-failed", { sOrderID: cancelRow?.sOrderID, error: String(err?.message || err).slice(0, 200) });
    return null;
  }
};

export const PREPARED_BILL_ON_SERVER_MESSAGE = (documentno, forPayment) =>
  forPayment
    ? `Bill ${documentno} was already prepared for this table. Press Pay again to settle it.`
    : `Bill ${documentno} was already prepared for this table. Reprinting it.`;
